import React, { useState, useEffect, useRef } from 'react';
import { Music, Volume2, VolumeX } from 'lucide-react';

interface MusicToggleProps {
  className?: string;
  fixed?: boolean;
  showDelay?: number;
  inHero?: boolean;
}

const MusicToggle: React.FC<MusicToggleProps> = ({ 
  className = '', 
  fixed = false,
  showDelay = 3000,
  inHero = false
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [playerReady, setPlayerReady] = useState(false);
  const [isSafari, setIsSafari] = useState(false);
  const playerRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Detectar Safari
  useEffect(() => {
    const ua = navigator.userAgent.toLowerCase();
    const isSafariOrMac = /^((?!chrome|android).)*safari/i.test(ua) || 
                         /mac os/i.test(ua);
    setIsSafari(isSafariOrMac);
  }, []);

  useEffect(() => {
    // Cargar la API de YouTube
    if (!window.YT) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);
    }

    // Función que se ejecutará cuando la API esté lista
    window.onYouTubeIframeAPIReady = () => {
      // Solo crear el player aquí, no intentar reproducir
      playerRef.current = new window.YT.Player('youtube-player', {
        height: '0',
        width: '0',
        videoId: 'kUsCwMejvPE', // ID del video de Carlos Vives
        playerVars: {
          // Importante: nunca autoplay=1 para Safari
          autoplay: 0,
          controls: 0,
          disablekb: 1,
          fs: 0,
          iv_load_policy: 3,
          modestbranding: 1,
          rel: 0,
          playsinline: 1
        },
        events: {
          onReady: (event) => {
            event.target.setVolume(30);
            setPlayerReady(true);
          },
        },
      });
    };

    // Si YT ya está cargado, inicializar directamente
    if (window.YT && typeof window.YT.Player === 'function') {
      window.onYouTubeIframeAPIReady();
    }

    // Mostrar botón después del delay
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, showDelay);

    return () => {
      clearTimeout(timer);
      // Detener el video si existe el player
      if (playerRef.current?.stopVideo) {
        try {
          playerRef.current.stopVideo();
        } catch (e) {
          console.log('Error al detener el video:', e);
        }
      }
    };
  }, [showDelay]);

  const toggleMusic = () => {
    if (!playerRef.current || !playerReady) return;

    try {
      if (isPlaying) {
        playerRef.current.pauseVideo();
        setIsPlaying(false);
      } else {
        // En Safari, necesitamos un enfoque diferente
        if (isSafari) {
          // Intenta reproducir con un pequeño retraso
          setTimeout(() => {
            try {
              playerRef.current.playVideo();
              // Verificar si se está reproduciendo realmente
              setTimeout(() => {
                const state = playerRef.current.getPlayerState();
                // Si no está reproduciendo (1 es PLAYING)
                if (state !== 1) {
                  console.log('Safari bloqueó la reproducción automática');
                } else {
                  setIsPlaying(true);
                }
              }, 500);
            } catch (e) {
              console.log('Error al reproducir en Safari:', e);
            }
          }, 100);
        } else {
          // Para otros navegadores
          playerRef.current.playVideo();
          setIsPlaying(true);
        }
      }
    } catch (e) {
      console.error('Error al controlar la música:', e);
    }
  };

  // Si está en el Hero o no es fijo, no aplicamos clases de posicionamiento fijo
  const positionClass = fixed && !inHero
    ? "fixed top-6 right-6 z-40" 
    : "";
    
  // Si está en el Hero y hay otra instancia fija visible, no mostramos esta
  if (inHero && fixed) {
    return null;
  }

  return (
    <>
      {/* Contenedor para el player de YouTube */}
      <div className="sr-only" aria-hidden="true">
        <div id="youtube-player"></div>
      </div>
      
      <div 
        className={`${positionClass} transition-all duration-500 transform ${
          isVisible ? 'opacity-100' : 'opacity-0'
        } ${className}`}
      >
        <button
          onClick={toggleMusic}
          className="flex items-center gap-2 px-4 py-2 rounded-full bg-cream/80 backdrop-blur-sm border border-sage text-forest hover:bg-cream transition-all duration-300 shadow-md group"
          aria-label={isPlaying ? "Pausar música" : "Activar música"}
        >
          <span className="text-sm font-dancing">
            {isPlaying ? "Música" : "Activar música"}
          </span>
          {isPlaying ? (
            <Music size={16} className="animate-pulse" />
          ) : (
            <VolumeX size={16} />
          )}
          <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-forest/30 scale-x-0 group-hover:scale-x-100 transition-transform origin-center duration-300"></span>
        </button>
      </div>
    </>
  );
};

// Agregar la definición para TypeScript
declare global {
  interface Window {
    onYouTubeIframeAPIReady: () => void;
    YT: any;
  }
}

export default MusicToggle;
